<template>
  <transition v-if="product" name="fade">
    <div
      v-if="!isCtaVisible"
      class="bg-white w-full shadow-top bottom-0 sticky z-1"
      :style="`--un-bg-opacity: ${$viewport.breakpoint === 'lg' ? '90%' : '100%'}; top: ${stickyHeaderTop}`"
    >
      <base-sticky name="stickyProductCta">
        <div ref="stickyContainerRef" class="px-4 py-4 flex gap-4 items-center wrap lg:px-6 lg:py-2" style="min-height: 4rem">
          <base-picture
            v-if="galleryItems[0].src"
            :alt="galleryItems[0].alt"
            class="shrink-0 h-16 w-a <lg:hidden"
            height="64"
            :src="galleryItems[0].src"
            width="59"
          />
          <template v-if="showStickyTitle">
            <p class="lg:subtitle-4 lg:grow subtitle-3" data-test-id="product-name">
              {{ product.name }}
            </p>
          </template>
          <template v-if="!isCtaVisible">
            <div class="flex <lg:w-full">
              <template v-if="isComingSoon || isOutOfStock">
                <product-notify-me v-if="isNotifyMe" v-style:min-w="`200px`" :size="{ sm: 'sm' }" />
                <vf-button
                  v-else
                  v-style:min-w="`200px`"
                  class="w-full"
                  disable-success-icon
                  disabled
                  :loading="pending"
                  :size="{ sm: 'sm' }"
                >
                  {{ isComingSoon ? $t.comingSoon : $t.outOfStock }}
                </vf-button>
              </template>
              <vf-button
                v-else
                aria-live="polite"
                class="flex-1"
                :class="{ 'c-white': isSignInToBuy && theme }"
                class-content="w-full between gap-16"
                :disabled="pending || addToCartLoading || checkoutCtaLoading"
                :loading="addToCartLoading && !checkoutCtaLoading"
                :size="{ sm: 'sm' }"
                :theme="isSignInToBuy ? theme : undefined"
                :variant="isSignInToBuy && theme ? 'themable' : 'primary'"
                @click="!selectedVariant
                  ? PanelVariantSelection.open({ attribute: variantAttributes[0] })
                  : addToCart()"
              >
                {{ ctaLabel }}
                <product-pricing
                  class-current="c-brand-5"
                  :currency="product.currency"
                  hide-strikethrough-price
                  :price="product.price"
                />
              </vf-button>
            </div>
          </template>
        </div>
      </base-sticky>
    </div>
  </transition>

  <panel-variant-selection :size="{ sm: 'sm', lg: 'full' }" />
</template>

<script setup lang="ts">
const { showStickyHeader } = useAppConfig().pages.pdp
const { card: { signInToBuyCta: { theme } } } = useAppConfig().components.product
const header = useHeaderStore()
const { $t, $viewport } = useNuxtApp()
const {
  addToCart,
  addToCartLoading,
  checkoutCtaLoading,
  galleryItems,
  isComingSoon,
  isNotifyMe,
  isOutOfStock,
  isSignInToBuy,
  pending,
  product,
  selectedVariant,
  attributeSelection
} = usePdp()
const stickyState = useSticky()
const stickyContainerRef = ref<HTMLElement>()
const PanelVariantSelection = createPanel('variant-selection')

const { height: windowHeight } = useWindowSize()
const { top: productDetailsCtaTop } = useElementBounding(() => stickyState.value.productDetailsCta)
const { y: productTitleY } = useElementBounding(useState('productDetailsTitleRef'))
const { height: stickyContainerHeight } = useElementBounding(stickyContainerRef)
const isCtaVisible = useElementVisibility(useState('productDetailsCtaRef'))

const ctaLabel = computed(() => {
  if (isSignInToBuy.value) return $t.signInToBuy
  if (selectedVariant.value) return $t.addToCart
  return $t.selectSize
})

const showStickyTitle = computed(() =>
  ($viewport.breakpoint === 'lg'
    || ($viewport.breakpoint === 'sm'
      && productTitleY.value > (windowHeight.value - stickyContainerHeight.value)))
)

const stickyHeaderTop = computed(() =>
  !showStickyHeader && !header.pageScrolledUp
    ? '0'
    : productDetailsCtaTop.value < 0 && !header.pageScrolledUp
      ? pxToRem(header.sticky.offset)
      : pxToRem(header.sticky.margin)
)

const variantAttributes = computed(() =>
  product.value?.attributes.filter((attribute) =>
    attribute.type !== 'color' && !attributeSelection[attribute.type]
  ) || []
)
</script>
